/**
 * Riassegnazione dell'ordine a un altro cliente (R6-C).
 *
 * Modello: `CambiaProduttoreModal` (#63) per il flusso "scegli esistente /
 * crea nuovo", ma costruita sul `Modal` generico del design system invece di
 * ri-scrivere lo scaffold overlay a mano.
 *
 * La creazione inline RIUSA `clienteCreateSchema` e `ClienteFormFields`: gli
 * stessi del form di creazione cliente, quindi valgono anche i validator
 * `almeno_piva_o_cf` e `azienda_richiede_codice_o_pec`. Nessuno schema ridotto.
 *
 * L'autorità resta il backend: qui si raccoglie l'intento e si mostra ciò che
 * il backend rifiuta o segnala.
 */
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Search, FilePlus, Plus, AlertTriangle, Ban } from 'lucide-react';
import Modal from './ui/Modal';
import ClienteFormFields from './ClienteFormFields';
import { identitaFiscale } from '../lib/identitaFiscale';
import { clienteCreateSchema, type ClienteCreateData } from '../schemas/cliente';
import { createCliente, searchClienti } from '../api/clienti';
import {
  cambiaClienteRichiesta,
  parseCambiaClienteConflitto,
  type CambiaClienteConflitto,
  type CambiaClientePayload,
  type RichiestaDetail,
} from '../api/richieste';
import type { Cliente } from '../types/cliente';
import { useDebounce } from '../hooks/useDebounce';

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

type Mode = 'esistente' | 'nuovo';

interface Props {
  richiestaId: string;
  clienteAttualeId: string;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
}

export default function CambiaClienteModal({
  richiestaId, clienteAttualeId, onClose, onSaved,
}: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('esistente');

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [suggestions, setSuggestions] = useState<Cliente[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selected, setSelected] = useState<Cliente | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Q3 — contatti richiesta precompilati dal cliente scelto, editabili.
  const [referente, setReferente] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflitto, setConflitto] = useState<CambiaClienteConflitto | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<ClienteCreateData>({
    resolver: zodResolver(clienteCreateSchema),
    defaultValues: {
      ragione_sociale: '', piva: '', codice_fiscale: '', indirizzo: '', cap: '',
      citta: '', provincia: '', nazione: 'IT', codice_destinatario: '',
      referente: '', telefono: '', email: '', pec: '',
    },
  });

  useEffect(() => {
    void (async () => {
      if (!debouncedQuery.trim() || mode !== 'esistente') {
        setSuggestions([]);
        return;
      }
      setLoadingSuggestions(true);
      try {
        const items = await searchClienti(debouncedQuery, 10);
        setSuggestions(items.filter((c) => c.id !== clienteAttualeId));
      } catch {
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    })();
  }, [debouncedQuery, mode, clienteAttualeId]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function seleziona(c: Cliente) {
    setSelected(c);
    setQuery(c.ragione_sociale);
    setShowDropdown(false);
    setSuggestions([]);
    // Precompila i contatti dal nuovo cliente, lasciandoli modificabili.
    setReferente(c.referente ?? '');
    setEmail(c.email ?? '');
    setTelefono(c.telefono ?? '');
  }

  /**
   * FIX 1 (R6-D) — si spediscono SEMPRE tutte e tre le chiavi: quello che si
   * vede nella modale è quello che l'ordine avrà. Omettere una chiave vuota
   * lasciava sull'ordine il contatto del cliente PRECEDENTE, cioè di un
   * soggetto diverso da quello ora intestatario.
   *
   * `referente_richiesta` è nullable a schema → vuoto significa `null`.
   * `email`/`telefono` sono NOT NULL: la UI li impone (vedi `contattiMancanti`),
   * il backend rifiuta comunque con 422 se arrivassero vuoti.
   */
  function payloadContatti(customerId: string, conferma = false): CambiaClientePayload {
    const p: CambiaClientePayload = {
      customer_id: customerId,
      referente_richiesta: referente.trim() || null,
      email_richiesta: email.trim(),
      telefono_richiesta: telefono.trim(),
    };
    if (conferma) p.conferma_riassegnazione = true;
    return p;
  }

  // Validazione client bloccante sui due contatti obbligatori. Vale solo dopo
  // aver scelto un cliente: prima non c'è nulla da compilare.
  const emailMancante = !!selected && !email.trim();
  const telefonoMancante = !!selected && !telefono.trim();
  const contattiIncompleti = emailMancante || telefonoMancante;

  async function riassegna(payload: CambiaClientePayload) {
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await cambiaClienteRichiesta(richiestaId, payload);
      onSaved(updated);
    } catch (err: unknown) {
      const c = parseCambiaClienteConflitto(err);
      if (c) {
        setConflitto(c);
        return;
      }
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : t('richieste.cambia_cliente.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  }

  async function onCreateSubmit(data: ClienteCreateData) {
    // `clienteCreateSchema` non impone il telefono, ma orders.telefono_richiesta
    // e' NOT NULL: si blocca qui, prima di creare il cliente, per non lasciare
    // un'anagrafica orfana se la riassegnazione venisse poi rifiutata.
    if (!data.telefono?.trim() || !data.email?.trim()) {
      setSaveError(t('richieste.cambia_cliente.contatti_obbligatori_creazione'));
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const creato = await createCliente({
        ragione_sociale: data.ragione_sociale,
        piva: data.piva?.trim() || undefined,
        codice_fiscale: data.codice_fiscale?.trim() || undefined,
        indirizzo: data.indirizzo?.trim() || undefined,
        cap: data.cap?.trim() || undefined,
        citta: data.citta?.trim() || undefined,
        provincia: data.provincia?.trim() || undefined,
        nazione: data.nazione?.trim() || undefined,
        codice_destinatario: data.codice_destinatario?.trim() || undefined,
        referente: data.referente?.trim() || undefined,
        telefono: data.telefono?.trim() || undefined,
        email: data.email,
        pec: data.pec?.trim() || undefined,
      });
      await riassegna({
        customer_id: creato.id,
        referente_richiesta: data.referente?.trim() || null,
        email_richiesta: data.email.trim(),
        telefono_richiesta: data.telefono.trim(),
      });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setSaveError(typeof detail === 'string' ? detail : t('richieste.cambia_cliente.errore_creazione'));
      setIsSaving(false);
    }
  }

  // ── Conflitto: bloccanti senza bypass, bozza con conferma ─────────────────
  if (conflitto) {
    const bloccante = conflitto.code !== 'conferma_riassegnazione_richiesta';
    return (
      <Modal
        title={t(`richieste.cambia_cliente.conflitto.${conflitto.code}.titolo`)}
        onClose={onClose}
        maxWidth="max-w-xl"
        testid="modal-cambia-cliente-conflitto"
        footer={
          <>
            <button type="button" onClick={() => setConflitto(null)} disabled={isSaving}
              className="px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors disabled:opacity-60">
              {t('common.azioni.annulla')}
            </button>
            {!bloccante && (
              <button
                type="button"
                data-testid="btn-conferma-riassegnazione"
                disabled={isSaving}
                onClick={() => {
                  const id = selected?.id;
                  if (!id) return;
                  setConflitto(null);
                  void riassegna(payloadContatti(id, true));
                }}
                className="px-3 py-1.5 text-xs font-medium text-white bg-accent-warning rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {t('richieste.cambia_cliente.conflitto.conferma_e_stacca')}
              </button>
            )}
          </>
        }
      >
        <div className="flex gap-2.5 text-sm text-ink-dark">
          {bloccante
            ? <Ban size={16} className="text-accent-danger flex-shrink-0 mt-0.5" />
            : <AlertTriangle size={16} className="text-accent-warning flex-shrink-0 mt-0.5" />}
          <p>{conflitto.message}</p>
        </div>

        {conflitto.code === 'fatture_attive_collegate' && (
          <ul className="border border-stone-mid rounded-md divide-y divide-stone-mid max-h-52 overflow-y-auto">
            {conflitto.fatture.map((f) => (
              <li key={f.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="font-mono text-ink-dark flex-1">{f.numero ?? '—'}</span>
                <span className="text-ink-soft text-xs">{formatDateIT(f.data)}</span>
                <span className="text-xs text-ink-mid capitalize">{f.stato}</span>
              </li>
            ))}
          </ul>
        )}

        {conflitto.code === 'fatture_passive_collegate' && (
          <ul className="border border-stone-mid rounded-md divide-y divide-stone-mid max-h-52 overflow-y-auto">
            {conflitto.fatture_passive.map((f) => (
              <li key={f.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="font-mono text-ink-dark flex-1">{f.numero ?? '—'}</span>
                <span className="text-ink-soft text-xs">{formatDateIT(f.data)}</span>
                <span className="text-xs text-ink-mid">
                  {f.pagata ? t('richieste.cambia_cliente.conflitto.pagata') : t('richieste.cambia_cliente.conflitto.non_pagata')}
                </span>
              </li>
            ))}
          </ul>
        )}

        {conflitto.code === 'conferma_riassegnazione_richiesta' && (
          <div>
            <p className="text-xs text-ink-soft mb-1.5">{t('richieste.cambia_cliente.conflitto.conferimenti_coinvolti')}</p>
            <p className="text-sm font-mono text-ink-dark">{conflitto.conferimenti.join(', ')}</p>
          </div>
        )}
      </Modal>
    );
  }

  // ── Flusso principale ─────────────────────────────────────────────────────
  const modeBtn = (m: Mode, label: string, Icon: typeof Search) => (
    <button
      type="button"
      onClick={() => { setMode(m); setSaveError(null); }}
      data-testid={`btn-mode-${m}`}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
        mode === m ? 'bg-sage text-white border-sage' : 'bg-white text-ink-mid border-stone-border hover:bg-stone-bg'
      }`}
    >
      <Icon size={13} /> {label}
    </button>
  );

  return (
    <Modal
      title={t('richieste.cambia_cliente.titolo')}
      subtitle={t('richieste.cambia_cliente.sottotitolo')}
      onClose={onClose}
      maxWidth="max-w-2xl"
      testid="modal-cambia-cliente"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          {mode === 'esistente' ? (
            <button
              type="button"
              data-testid="btn-riassegna"
              onClick={() => selected && void riassegna(payloadContatti(selected.id))}
              disabled={isSaving || !selected || contattiIncompleti}
              className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t('common.azioni.salvataggio_in_corso') : t('richieste.cambia_cliente.riassegna')}
            </button>
          ) : (
            <button
              type="button"
              data-testid="btn-crea-e-riassegna"
              onClick={handleSubmit(onCreateSubmit)}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
            >
              <Plus size={14} />
              {isSaving ? t('common.azioni.salvataggio_in_corso') : t('richieste.cambia_cliente.crea_e_riassegna')}
            </button>
          )}
        </>
      }
    >
      <div className="flex flex-wrap gap-2">
        {modeBtn('esistente', t('richieste.cambia_cliente.mode_esistente'), Search)}
        {modeBtn('nuovo', t('richieste.cambia_cliente.mode_nuovo'), FilePlus)}
      </div>

      {/* FIX 4 (R6-D): altezza minima riservata, così i primi ~5 risultati
          dell'autocomplete sono visibili senza scroll interno. Il dropdown è in
          position:absolute dentro il body del Modal, che ha overflow-y-auto:
          senza spazio riservato verrebbe tagliato. */}
      {mode === 'esistente' && (
        <div className="min-h-[22rem] space-y-4">
          <div>
            <label className="block text-xs text-ink-soft mb-1">{t('richieste.cambia_cliente.cerca_label')}</label>
            <div className="relative" ref={dropdownRef}>
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
              <input
                type="text"
                data-testid="input-cerca-cliente"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSelected(null); setShowDropdown(true); }}
                onFocus={() => query && setShowDropdown(true)}
                placeholder={t('richieste.cambia_cliente.cerca_placeholder')}
                className={`${inputCls} pl-8`}
              />
              {showDropdown && query.trim().length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-stone-border rounded-lg shadow-lg max-h-[17rem] overflow-y-auto">
                  {loadingSuggestions ? (
                    <div className="px-3 py-2.5 text-sm text-ink-soft">{t('common.azioni.caricamento')}</div>
                  ) : suggestions.length === 0 ? (
                    <div className="px-3 py-2.5 text-sm text-ink-faint">{t('richieste.cambia_cliente.nessun_risultato')}</div>
                  ) : (
                    <ul>
                      {suggestions.map((c) => (
                        <li key={c.id}>
                          <button type="button" data-testid="opzione-cliente" onClick={() => seleziona(c)}
                            className="w-full text-left px-3 py-2.5 hover:bg-stone-bg transition-colors">
                            <div className="text-sm font-medium text-ink-dark">{c.ragione_sociale}</div>
                            {identitaFiscale(c) && (
                              <div className="text-xs text-ink-soft font-mono">{identitaFiscale(c)}</div>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            {selected && (
              <p className="mt-1.5 text-xs text-accent-success font-medium" data-testid="cliente-selezionato">
                {t('richieste.cambia_cliente.selezionato')} {selected.ragione_sociale}
              </p>
            )}
          </div>

          {selected && (
            <div className="space-y-2 pt-1">
              <p className="text-xs text-ink-soft">{t('richieste.cambia_cliente.contatti_titolo')}</p>
              <div className="grid grid-cols-3 gap-3 items-start">
                <div>
                  <input className={inputCls} data-testid="input-contatto-referente"
                    value={referente} onChange={(e) => setReferente(e.target.value)}
                    placeholder={t('anagrafiche.campi.referente')} />
                </div>
                <div>
                  <input
                    className={`${inputCls} ${emailMancante ? 'border-accent-danger' : ''}`}
                    data-testid="input-contatto-email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={`${t('anagrafiche.campi.email')} *`}
                  />
                  {emailMancante && (
                    <p className="text-xs text-accent-danger mt-0.5" data-testid="errore-contatto-email">
                      {t('richieste.cambia_cliente.contatto_obbligatorio')}
                    </p>
                  )}
                </div>
                <div>
                  <input
                    className={`${inputCls} ${telefonoMancante ? 'border-accent-danger' : ''}`}
                    data-testid="input-contatto-telefono" value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    placeholder={`${t('anagrafiche.campi.telefono')} *`}
                  />
                  {telefonoMancante && (
                    <p className="text-xs text-accent-danger mt-0.5" data-testid="errore-contatto-telefono">
                      {t('richieste.cambia_cliente.contatto_obbligatorio')}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-ink-faint">{t('richieste.cambia_cliente.contatti_hint')}</p>
            </div>
          )}
        </div>
      )}

      {mode === 'nuovo' && <ClienteFormFields register={register} errors={errors} />}

      {saveError && (
        <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
          {saveError}
        </div>
      )}
    </Modal>
  );
}
